import { useFormik } from 'formik'
import { ChangeEvent, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../../components/button'
import { BackButton } from '../../../components/button/backButton'
import { ActionChip } from '../../../components/cards/actionChip'
import { NavMenuArticles } from '../../../components/channelCuration/NavMenuArticles'
import { NavMenuItems } from '../../../components/channelCuration/NavMenuItems'
import { ImageBanner } from '../../../components/images/imageBanner'
import ComboBox from '../../../components/inputs/combobox'
import { EditInput } from '../../../components/inputs/editInput'
import { ImageUploadPreviewModal } from '../../../components/modal/app_modals/ImagePreviewModal'
import { SelectFulfilmentCenterModal } from '../../../components/modal/app_modals/selectFulfilmentCenter'
import Spinner from '../../../components/spinner/Spinner'
import { PageHeader } from '../../../components/typography/PageHeader'
import { removeElementAtIndex } from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import Layout from '../../../layout'
import {
  useCreateListMenuMutation,
  useFetchListMenuQuery,
  useUpdateListMenuMutation,
} from '../../../redux-toolkits/list-menu/listMenu.slice'
import type {
  ListMenuItem,
  ListMenuItemEntry,
} from '../../../redux-toolkits/list-menu/listMenu.types'
import { useGetFulfilmentCentersQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import { FulfilmentCentre } from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { CreateListMenuSchema } from '../../../validation/validate'

export const SingleNavMenuPage = ({ mode }: { mode: 'create' | 'edit' }) => {
  const [editIndex, setEditIndex] = useState<number>()
  const [uploadedImage, setUploadedImage] = useState<File>()
  const [uploadedIcon, setUploadedIcon] = useState<File>()
  const [uploadMode, setUploadMode] = useState('')

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isCreate = mode === 'create' || id === 'new'

  const { data, isLoading, refetch } = useFetchListMenuQuery(id ?? '', {
    skip: isCreate,
  })
  const menu = data?.data

  const { data: fulfilmentCentresData } = useGetFulfilmentCentersQuery({
    page: 1,
    limit: 1000,
  })

  const [updateListMenu, { isLoading: isUpdating }] =
    useUpdateListMenuMutation()
  const [createListMenu, { isLoading: isCreatingMenu }] =
    useCreateListMenuMutation()

  const isSaving = isCreate ? isCreatingMenu : isUpdating

  const prefilledFulfilmentCentres = useMemo<FulfilmentCentre[]>(() => {
    return (menu?.fulfilmentCentre ?? [])
      .map((id) =>
        (fulfilmentCentresData?.data.docs ?? []).find((fc) => fc._id === id),
      )
      .filter((fc): fc is FulfilmentCentre => !!fc)
  }, [menu?.fulfilmentCentre, fulfilmentCentresData?.data.docs])

  const formik = useFormik({
    initialValues: {
      name: menu?.name ?? '',
      description: menu?.description ?? '',
      icon: menu?.icon ?? '',
      banner: menu?.banner ?? '',
      isActive: menu?.isActive ?? false,
      articles: (menu?.articles ?? []) as ListMenuItem['articles'],
      items: (menu?.items ?? []) as ListMenuItemEntry[],
      fulfilmentCentre: prefilledFulfilmentCentres,
    },
    validationSchema: CreateListMenuSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (isCreate) {
        const result = await createListMenu({
          name: values.name,
          description: values.description,
          articles: values.articles.map((a) => a._id),
          items: values.items,
          icon: values.icon || undefined,
          banner: values.banner || undefined,
          fulfilmentCentre: values.fulfilmentCentre.map((fc) => fc._id),
        })
        if (result?.data?.success) {
          navigate(`/channel-curation/nav-menu/${result.data.data._id}`, {
            replace: true,
          })
        }
      } else {
        if (!id) return
        const result = await updateListMenu({
          id,
          body: {
            name: values.name,
            description: values.description,
            icon: values.icon || undefined,
            banner: values.banner || undefined,
            isActive: values.isActive,
          },
        })
        if (result?.data?.success) {
          setEditIndex(undefined)
        }
      }
    },
  })

  const createModeMenu: ListMenuItem = {
    _id: '',
    name: formik.values.name,
    description: formik.values.description,
    slug: '',
    slugHistory: [],
    position: 0,
    listCount: 0,
    isActive: false,
    articles: formik.values.articles,
    items: formik.values.items,
    createdAt: '',
    updatedAt: '',
    __v: 0,
  }

  const displayMenu = isCreate ? createModeMenu : menu

  const handleFileChange = (
    e: ChangeEvent<HTMLInputElement>,
    mode: 'banner' | 'icon',
  ) => {
    if (!e.target.files) return
    setUploadMode(mode)
    const file = e.target.files[0]
    mode === 'banner' ? setUploadedImage(file) : setUploadedIcon(file)
    handleModalOpen('imageUploadPreviewModal')
  }

  return (
    <Layout>
      <BackButton text="Back to Nav Menu" className="mb-6" />

      {!isCreate && isLoading ? (
        <Spinner fullScreen={false} />
      ) : (
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PageHeader
                title={isCreate ? 'New List Menu' : menu?.name ?? 'List Menu'}
                className="!mb-0"
              />
            </div>

            <div className="flex items-center gap-3">
              <Button
                type="button"
                label="Cancel"
                size="medium"
                className="!px-8"
                onClick={() => {
                  formik.resetForm()
                  setEditIndex(undefined)
                }}
              />
              <Button
                type="submit"
                label="Save"
                primary
                size="medium"
                className="!px-8"
                loading={isSaving}
                disabled={
                  isSaving || !formik.isValid || (!isCreate && !formik.dirty)
                }
              />
            </div>
          </div>

          <div className="relative">
            <ImageBanner
              imageUrl={formik.values.banner}
              loading={false}
              errors={formik.errors.banner}
              onImageEdit={(e) => handleFileChange(e, 'banner')}
              editIconPos="top-right"
            />
            <ImageBanner
              imageUrl={formik.values.icon}
              loading={false}
              className="!rounded-full !w-[80px] !h-[80px] !absolute left-4 -bottom-[20px] z-10"
              errors={formik.errors.icon}
              onImageEdit={(e) => handleFileChange(e, 'icon')}
            />
          </div>

          <div className="flex flex-col gap-10">
            <p className="font-semibold text-base">Basic Information</p>

            <EditInput
              title="Name"
              value={formik.values.name}
              inputName="name"
              onChange={formik.handleChange}
              placeholder="List menu name"
              mode={isCreate ? 'new' : 'edit'}
              editing={editIndex === 0}
              onEditClick={() => setEditIndex(0)}
              onCancel={() => {
                formik.setFieldValue('name', menu?.name ?? '')
                setEditIndex(undefined)
              }}
              onEditSave={() => setEditIndex(undefined)}
              loading={false}
              errors={
                formik.touched.name && formik.errors.name
                  ? formik.errors.name
                  : ''
              }
              buttonText="Done"
            />

            <EditInput
              title="Description"
              value={formik.values.description}
              inputName="description"
              type="textarea"
              onChange={formik.handleChange}
              placeholder="Short description"
              mode={isCreate ? 'new' : 'edit'}
              editing={editIndex === 1}
              onEditClick={() => setEditIndex(1)}
              onCancel={() => {
                formik.setFieldValue('description', menu?.description ?? '')
                setEditIndex(undefined)
              }}
              onEditSave={() => setEditIndex(undefined)}
              loading={false}
              errors={
                formik.touched.description && formik.errors.description
                  ? formik.errors.description
                  : ''
              }
              buttonText="Done"
            />

            <EditInput
              title="Fulfilment Centre"
              onEditClick={
                isCreate
                  ? () => handleModalOpen('selectFulfilmentCenterModal')
                  : undefined
              }
              onCancel={() => {}}
              onEditSave={() => {}}
              editing={false}
              errors={
                formik.touched.fulfilmentCentre &&
                formik.errors.fulfilmentCentre
                  ? (formik.errors.fulfilmentCentre as string)
                  : undefined
              }
            >
              {() =>
                formik.values.fulfilmentCentre.length < 1 ? (
                  <p className="text-sec-black text-sm">
                    No fulfilment centres selected
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {formik.values.fulfilmentCentre.map((fc, idx) => (
                      <ActionChip
                        key={fc._id}
                        name={fc.name}
                        onDismiss={
                          isCreate
                            ? () => {
                                formik.setFieldValue(
                                  'fulfilmentCentre',
                                  removeElementAtIndex(
                                    [...formik.values.fulfilmentCentre],
                                    idx,
                                  ),
                                )
                              }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )
              }
            </EditInput>

            {!isCreate && (
              <EditInput
                title="Status"
                editing={editIndex === 2}
                onEditClick={() => setEditIndex(2)}
                onCancel={() => setEditIndex(undefined)}
                onEditSave={() => setEditIndex(undefined)}
                buttonText="Done"
              >
                {({ isEditing }) =>
                  isEditing ? (
                    <ComboBox
                      value={formik.values.isActive ? 'Published' : 'Draft'}
                      readOnly
                      hasIcon={false}
                      immediate
                      selected={formik.values.isActive ? 'published' : 'draft'}
                      menuItems={[
                        { name: 'Draft', value: 'draft' },
                        { name: 'Published', value: 'published' },
                      ]}
                      onSelectOption={(val: string) => {
                        formik.setFieldValue('isActive', val === 'published')
                        setEditIndex(undefined)
                      }}
                      isDropdown
                      position="bottom start"
                    />
                  ) : (
                    <p className="text-sec-black">
                      {formik.values.isActive ? 'Published' : 'Draft'}
                    </p>
                  )
                }
              </EditInput>
            )}
          </div>

          {displayMenu && (
            <NavMenuArticles
              menu={displayMenu}
              fulfilmentCentres={formik.values.fulfilmentCentre}
              onRefetch={refetch}
              onChange={
                isCreate
                  ? (articles) => formik.setFieldValue('articles', articles)
                  : undefined
              }
            />
          )}

          {displayMenu && (
            <NavMenuItems
              menu={displayMenu}
              fulfilmentCentres={formik.values.fulfilmentCentre}
              onRefetch={refetch}
              onChange={
                isCreate
                  ? (items) => formik.setFieldValue('items', items)
                  : undefined
              }
            />
          )}

          {isCreate && (
            <SelectFulfilmentCenterModal
              physicalOnly={false}
              open={modal === 'selectFulfilmentCenterModal' && modalOpen}
              setOpen={setModalOpen}
              onCloseCallback={() => handleModalClose()}
              onSuccessCallback={(centers, resetForm) => {
                formik.setFieldValue('fulfilmentCentre', centers)
                resetForm()
                handleModalClose()
              }}
              selectedCenters={formik.values.fulfilmentCentre}
              maxSelection={1}
            />
          )}

          {uploadedImage || uploadedIcon ? (
            <ImageUploadPreviewModal
              image={(uploadedImage || uploadedIcon) as File}
              open={modal === 'imageUploadPreviewModal' && modalOpen}
              setOpen={setModalOpen}
              onCloseCallback={() => {
                uploadedImage
                  ? setUploadedImage(undefined)
                  : setUploadedIcon(undefined)
                handleModalClose()
              }}
              uploadMode={uploadMode}
              onSuccessCallback={(url) => {
                formik.setFieldValue(uploadedImage ? 'banner' : 'icon', url)
              }}
            />
          ) : null}
        </form>
      )}
    </Layout>
  )
}
